export const LEAD_OPTIONS = [
  { value: "15d", label: "15\u00A0días" },
  { value: "1m", label: "1\u00A0mes" },
  { value: "3m", label: "3\u00A0meses" },
];

export const DIFF_TOLERANCE_DAYS = 2;

export const toDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input) ? null : input;
  const d = new Date(input);
  return isNaN(d) ? null : d;
};

export const toYmd = (input) => {
  const d = toDate(input);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatDDMMYYYY = (input) => {
  const d = toDate(input);
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

export const subDays = (input, days) => {
  const d = toDate(input);
  if (!d) return null;
  const out = new Date(d);
  out.setDate(out.getDate() - Number(days || 0));
  return out;
};

export const subMonths = (input, months) => {
  const d = toDate(input);
  if (!d) return null;
  const out = new Date(d);
  out.setMonth(out.getMonth() - Number(months || 0));
  return out;
};

export const parseYmdUTC = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
};
export const diffDaysYmd = (endYMD, startYMD) => {
  const end = parseYmdUTC(endYMD);
  const start = parseYmdUTC(startYMD);
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
};
export const inferTokenFromDateToShow = (expirationYMD, showYMD, tol = DIFF_TOLERANCE_DAYS) => {
  const delta = diffDaysYmd(expirationYMD, showYMD);
  if (Math.abs(delta - 90) <= tol) return "3 meses";
  if (Math.abs(delta - 30) <= tol) return "1 mes";
  if (Math.abs(delta - 15) <= tol) return "15 dias";
  return null;
};

const normalizeExpiration = (expiration) => {
  if (!expiration) return null;

  if (typeof expiration === "string") {
    const [y, m, d] = expiration.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  return new Date(expiration);
};

export const computeDateToShowByToken = (expiration, token) => {
  const base = normalizeExpiration(expiration);
  if (!base || !token) return null;

  const s = String(token || "").trim().toLowerCase();

  if (/^\d+d$/.test(s)) return subDays(base, parseInt(s, 10));
  if (/^\d+m$/.test(s)) return subMonths(base, parseInt(s, 10));

  return null;
};

export const buildLeadPreviews = (expiration, tokens = []) =>
  tokens
    .map((tok) => {
      const d = computeDateToShowByToken(expiration, tok);
      return d ? { token: tok, date: d, ymd: toYmd(d), pretty: formatDDMMYYYY(d) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.ymd.localeCompare(b.ymd));

export const isReminderDatePast = (expiration, token, today = new Date()) => {
  const reminderYmd = toYmd(computeDateToShowByToken(expiration, token));
  const todayYmd = typeof today === "string" ? today : toYmd(today);
  return Boolean(reminderYmd && todayYmd && reminderYmd < todayYmd);
};

export const monthsBefore = (dateInput, months) => subMonths(dateInput, months);
export const isVisibleByFrontRules = (userNotification) => {
  if (userNotification?.is_read) return false;
  const exp = userNotification?.Notification?.HealthSafetyReport?.expiration_date;
  if (!exp) return false;
  const now = new Date();
  const threshold = monthsBefore(exp, 3);
  return !!threshold && now >= threshold;
};

export const KEYS = ["company_fk", "branch_fk", "place_fk", "zone_fk", "local_fk"];

export const getDepthByIds = (idsObj = {}) => {
  for (let i = KEYS.length - 1; i >= 0; i--) if (idsObj[KEYS[i]] != null) return i;
  return -1;
};
export const deepestSelectedIdx = (sel) => getDepthByIds(sel);
export const getUserOrgIds = (u) => ({
  company_fk: u?.id_company ?? u?.company_fk ?? u?.company_id ?? null,
  branch_fk:
    u?.id_branch ?? u?.id_branch_offices ?? u?.branch_fk ?? u?.branch_id ?? null,
  place_fk:
    u?.id_place ?? u?.id_branch_office_place ?? u?.place_fk ?? u?.place_id ?? null,
  zone_fk: u?.id_zone ?? u?.id_place_zone ?? u?.zone_fk ?? u?.zone_id ?? null,
  local_fk: u?.id_local ?? u?.id_zone_local ?? u?.local_fk ?? u?.local_id ?? null,
});

const normalizeOrgScope = (scope = {}) => ({
  company_fk: scope?.company_fk ?? scope?.id_company ?? scope?.company_id ?? null,
  branch_fk:
    scope?.branch_fk ??
    scope?.branch_office ??
    scope?.id_branch ??
    scope?.id_branch_offices ??
    scope?.branch_id ??
    null,
  place_fk:
    scope?.place_fk ??
    scope?.place ??
    scope?.id_place ??
    scope?.id_branch_office_place ??
    scope?.place_id ??
    null,
  zone_fk:
    scope?.zone_fk ??
    scope?.zone ??
    scope?.id_zone ??
    scope?.id_place_zone ??
    scope?.zone_id ??
    null,
  local_fk:
    scope?.local_fk ??
    scope?.local ??
    scope?.id_local ??
    scope?.id_zone_local ??
    scope?.local_id ??
    null,
});

export const getUserOrgScopes = (user = {}) => {
  const relationGroups = [
    user?.user_companies,
    user?.userCompanies,
    user?.User_companies,
    user?.UserCompanies,
  ];
  const relations = relationGroups.find(Array.isArray) || [];
  const scopes = relations.map(normalizeOrgScope);

  if (!scopes.length) scopes.push(getUserOrgIds(user));

  const seen = new Set();
  return scopes.filter((scope) => {
    if (getDepthByIds(scope) < 0) return false;
    const key = KEYS.map((item) => scope[item] ?? "").join(":");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const organizationScopeMatchesSelection = (userScope, sel) => {
  const selDepth = getDepthByIds(sel);
  if (selDepth < 0) return false;
  const userDepth = getDepthByIds(userScope);
  if (userDepth < 0) return false;
  if (selDepth >= 1 && userDepth < 1) return false;

  const compareDepth = Math.min(selDepth, userDepth);
  for (let i = 0; i <= compareDepth; i++) {
    const s = sel[KEYS[i]];
    const u = userScope[KEYS[i]];
    if (s != null && u != null && String(s) !== String(u)) return false;
  }
  return true;
};

export const userMatchesOrganizationScope = (user, sel) =>
  isMaster(user) ||
  getUserOrgScopes(user).some((scope) =>
    organizationScopeMatchesSelection(scope, sel),
  );

export const filterUsersByOrganizationScope = (users = [], sel = {}) =>
  (Array.isArray(users) ? users : []).filter((user) =>
    userMatchesOrganizationScope(user, sel),
  );

export const userMatchesSelectionByIds = (userIds, sel) => {
  const selDepth = getDepthByIds(sel);
  if (selDepth < 0) return false;
  const userDepth = getDepthByIds(userIds);
  if (selDepth < userDepth) return false;
  for (let i = 0; i <= userDepth; i++) {
    const s = sel[KEYS[i]];
    const u = userIds[KEYS[i]];
    if (s != null && u != null && String(s) !== String(u)) return false;
  }
  return true;
};
export const isMaster = (u) => String(u?.privilege).toLowerCase() === "master";

export const slugCat = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[ - ]/g, "")
    .replace(/[()\/]/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export const CATEGORY_TREE = {
  Mediciones: [
    "PUESTA A TIERRA",
    "DIFERENCIALES",
    "TERMOGRAFÍA",
    "PAT Y DIFERENCIALES",
  ],
  Mantenimiento: ["Preventivo", "Correctivo", "Calibración de equipos"],
  Certificados: ["CERTIFICADOS"],
  Remitos: ["REMITOS"],
  Instalaciones: [],
};

export const toUpper = (s) => (s == null ? "" : String(s).trim().toUpperCase());
export const toUpperLoose = (s) => (s == null ? "" : String(s).toUpperCase());

export const ymdToDateUTC = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
export const dateUTCToYMD = (date) => {
  if (!date) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
export const subtractDaysYMD = (ymd, days) => {
  const d = ymdToDateUTC(ymd);
  if (!d) return "";
  d.setUTCDate(d.getUTCDate() - days);
  return dateUTCToYMD(d);
};
export const subtractMonthsYMD = (ymd, months) => {
  const d = ymdToDateUTC(ymd);
  if (!d) return "";
  d.setUTCMonth(d.getUTCMonth() - months);
  return dateUTCToYMD(d);
};
export const computeDateToShow = (expirationYMD, token) => {
  const s = String(token).trim().toLowerCase();
  if (/^\d+\s*d$/.test(s)) return subtractDaysYMD(expirationYMD, parseInt(s, 10));
  if (/^\d+\s*m$/.test(s)) return subtractMonthsYMD(expirationYMD, parseInt(s, 10));
  if (/^\d+$/.test(s)) return subtractDaysYMD(expirationYMD, parseInt(s, 10));
  return null;
};

export const STORAGE_KEY = "hs_category_tree_v1";
export const normalizeTreeUpper = (treeObj) => {
  const out = {};
  Object.keys(treeObj || {}).forEach((cat) => {
    const CAT = toUpper(cat);
    const subs = Array.isArray(treeObj[cat]) ? treeObj[cat] : [];
    out[CAT] = Array.from(new Set(subs.map(toUpper)));
  });
  return out;
};
export const mergeCategoryTreesUpper = (baseTree, savedTree) => {
  const baseU = normalizeTreeUpper(baseTree || {});
  const savedU = normalizeTreeUpper(savedTree || {});
  const out = {};
  const cats = Array.from(new Set([...Object.keys(baseU), ...Object.keys(savedU)]));
  cats.forEach((c) => {
    out[c] = Array.from(new Set([...(baseU[c] || []), ...(savedU[c] || [])]));
  });
  return out;
};
export const loadTreeFromLocalStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
export const saveTreeToLocalStorage = (tree) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tree));
  } catch {}
};
export const toAdvanceTextFromLabel = (label) =>
  String(label || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const subtractDays = (isoDate, days) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d)) return null;
  d.setDate(d.getDate() - days);
  return d;
};
export const subtractMonths = (isoDate, months) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d)) return null;
  d.setMonth(d.getMonth() - months);
  return d;
};

export const stripAccentsLower = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const TOKEN_TO_LABEL = new Map(LEAD_OPTIONS.map((o) => [o.value, stripAccentsLower(o.label)]));

export const LABEL_TO_TOKEN = new Map(LEAD_OPTIONS.map((o) => [stripAccentsLower(o.label), o.value]));

export const formatYMD_DDMMYYYY = (v) => {
  if (!v) return "";
  const s = String(v).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [yy, mm, dd] = s.split("-");
  return `${dd}-${mm}-${yy}`;
};

export const normalizeNotification = (n) => {
  const flat = {
    id: n.user_notification_id || n.id || n.notification_id,
    title: n.title ?? n.Notification?.title ?? "",
    text: n.text ?? n.Notification?.text ?? "",
    date_to_show: (n.date_to_show ?? n.Notification?.date_to_show ?? "").slice(0, 10),
    is_read: n.is_read ?? n.Notification?.is_read ?? false,
    report_fk:
      n.report_fk ??
      n.Notification?.report_fk ??
      n.healthSafetyReport?.id ??
      n.Notification?.HealthSafetyReport?.id ??
      null,
    type: n.type ?? n.Notification?.type ?? null,
    healthSafetyReport: n.healthSafetyReport ?? n.Notification?.HealthSafetyReport ?? null,
  };
  return flat;
};

export const buildIdToOption = (users = []) => {
  const m = new Map();
  [...users].forEach((u) => {
    m.set(u.id_user, {
      value: u.id_user,
      label: `${u.user}${u.privilege ? ` (${u.privilege})` : ""}`,
      data: { isMaster: isMaster(u), user: u },
    });
  });
  return m;
};

export const buildUserOptions = (nonMasterUsers = [], masterUsers = []) => {
  const ALL_OPTION = { value: "__ALL__", label: "Seleccionar todos los usuarios" };
  const toOpt = (u) => ({
    value: u.id_user,
    label: `${u.user}${u.privilege ? ` (${u.privilege})` : ""}`,
    data: { isMaster: isMaster(u), user: u },
  });
  return [ALL_OPTION, ...nonMasterUsers.map(toOpt), ...masterUsers.map(toOpt)];
};

export const addUniqueFiles = (existingFiles = [], newFiles = []) => {
  const existingNames = new Set(existingFiles.map((f) => f.name));
  const uniqueNew = (newFiles || []).filter((f) => !existingNames.has(f.name));
  return [...existingFiles, ...uniqueNew];
};
